import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';

export default function PublicWorkerCard() {
  const { regNumber } = useParams();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/lookup/worker-card/${regNumber}`)
      .then(r => setData(r.data.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [regNumber]);

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-900 flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-4 border-blue-300 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-blue-900 flex flex-col items-center justify-center p-6 text-center">
        <span className="text-5xl">🔍</span>
        <h1 className="mt-4 text-xl font-bold text-white">Card Not Found</h1>
        <p className="text-blue-300 mt-2 text-sm">This worker card doesn't exist or has been removed.</p>
        <Link to="/" className="mt-4 text-blue-200 text-sm underline hover:text-white">Go to naukarihaat.com</Link>
      </div>
    );
  }

  const isActive   = data.is_active;
  const isVerified = data.verification_status === 'verified';
  const validTill  = data.expiry_date
    ? new Date(data.expiry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';
  // proxy handles /uploads/... → backend; no port manipulation needed
  const photoUrl = data.photo_url || null;
  const skills   = (data.skills   || []).map(s => typeof s === 'string' ? s : (s.skill_name || s.name)).filter(Boolean);
  const langs    = (data.languages || []).map(l => typeof l === 'string' ? l : (l.language_name || l.name)).filter(Boolean);

  const age = data.age ?? (data.dob
    ? Math.floor((Date.now() - new Date(data.dob)) / (365.25 * 24 * 3600 * 1000))
    : null);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-950 to-blue-800 flex flex-col items-center justify-center p-4 py-10">

      <div className="w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl border border-blue-700/30">

        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-900 via-blue-800 to-blue-700 px-4 pt-4 pb-3 overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shadow">
                <span className="text-blue-800 font-extrabold text-sm leading-none">NH</span>
              </div>
              <div>
                <p className="text-white font-extrabold text-[13px] tracking-wide leading-none">naukarihaat.com</p>
                <p className="text-blue-200 text-[9px] tracking-widest uppercase mt-0.5">Worker Identity Card</p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-0.5">
              {isVerified ? (
                <span className="inline-flex items-center gap-1 bg-green-400 text-green-900 text-[9px] font-extrabold px-2 py-0.5 rounded-full">
                  <svg className="w-2 h-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  VERIFIED
                </span>
              ) : (
                <span className="inline-flex items-center bg-yellow-400 text-yellow-900 text-[9px] font-extrabold px-2 py-0.5 rounded-full">PENDING</span>
              )}
              {!isActive && (
                <span className="inline-flex items-center bg-red-500 text-white text-[9px] font-extrabold px-2 py-0.5 rounded-full">EXPIRED</span>
              )}
              {isActive && (
                <p className="text-blue-200 text-[8px]">Valid till {validTill}</p>
              )}
            </div>
          </div>
        </div>

        {/* Stripe */}
        <div className="h-1.5 bg-gradient-to-r from-blue-500 via-blue-400 to-sky-400" />

        {/* Body */}
        <div className="bg-white px-4 py-4 space-y-3">
          {/* Photo + Info */}
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <div className="w-20 h-24 rounded-xl border-2 border-blue-700 overflow-hidden bg-blue-50 shadow-md">
                {photoUrl ? (
                  <img src={photoUrl} alt={data.full_name} className="w-full h-full object-cover object-top" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-4xl font-extrabold text-blue-200">
                      {data.full_name?.[0]?.toUpperCase() || '?'}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 min-w-0 space-y-1.5">
              <h1 className="font-extrabold text-gray-900 text-sm uppercase tracking-wide leading-tight">
                {data.full_name}
              </h1>
              <table className="text-[10px] text-gray-600 w-full">
                <tbody>
                  {age && (
                    <tr>
                      <td className="text-gray-400 pr-1.5 pb-0.5 whitespace-nowrap">Age</td>
                      <td className="font-semibold text-gray-800">{age} Years</td>
                    </tr>
                  )}
                  {data.gender && (
                    <tr>
                      <td className="text-gray-400 pr-1.5 pb-0.5">Gender</td>
                      <td className="font-semibold text-gray-800 capitalize">{data.gender}</td>
                    </tr>
                  )}
                  {data.experience_years != null && (
                    <tr>
                      <td className="text-gray-400 pr-1.5 pb-0.5">Exp.</td>
                      <td className="font-semibold text-gray-800">{data.experience_years} yr{data.experience_years !== 1 ? 's' : ''}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {(data.city_village || data.district || data.state) && (
                <div className="flex items-start gap-1">
                  <span className="text-[10px] mt-px">📍</span>
                  <p className="text-[10px] text-gray-600 leading-tight">
                    {[data.city_village, data.district, data.state].filter(Boolean).join(', ')}
                    {data.pincode ? ` – ${data.pincode}` : ''}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-dashed border-gray-200" />

          {skills.length > 0 && (
            <div>
              <p className="text-[8px] font-bold text-blue-600 uppercase tracking-widest mb-1.5">Skills</p>
              <div className="flex flex-wrap gap-1">
                {skills.slice(0, 8).map(s => (
                  <span key={s} className="text-[10px] bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full font-semibold">{s}</span>
                ))}
              </div>
            </div>
          )}

          {langs.length > 0 && (
            <div>
              <p className="text-[8px] font-bold text-blue-600 uppercase tracking-widest mb-1.5">Languages</p>
              <div className="flex flex-wrap gap-1">
                {langs.map(l => (
                  <span key={l} className="text-[10px] bg-gray-50 text-gray-700 border border-gray-200 px-2 py-0.5 rounded-full">{l}</span>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-dashed border-gray-200" />

          <div className="flex items-end justify-between">
            <div>
              <p className="text-[8px] text-gray-400 font-bold uppercase tracking-widest">Registration No.</p>
              <p className="text-[11px] font-mono font-bold text-gray-800 tracking-wider mt-0.5">{data.registration_number}</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 border-2 border-gray-200 rounded-lg flex items-center justify-center bg-gray-50">
                <svg viewBox="0 0 24 24" className="w-7 h-7 text-gray-400" fill="currentColor">
                  <path d="M3 3h7v7H3V3zm2 2v3h3V5H5zm9-2h7v7h-7V3zm2 2v3h3V5h-3zM3 14h7v7H3v-7zm2 2v3h3v-3H5zm9-2h2v2h-2v-2zm2 2h2v2h-2v-2zm2-2h2v2h-2v-2zm-4 4h2v2h-2v-2zm2 2h2v-2h2v4h-4v-2zm2-6h2v2h-2v-2z"/>
                </svg>
              </div>
              <p className="text-[7px] text-gray-400 mt-0.5">Scan to verify</p>
            </div>
          </div>
        </div>

        {/* Card footer */}
        <div className="bg-blue-900 px-4 py-2 flex items-center justify-between">
          <p className="text-blue-300 text-[8px] tracking-wide">www.naukarihaat.com</p>
          <p className="text-blue-400 text-[8px]">Your bridge to better work</p>
        </div>
      </div>

      {/* Page footer */}
      <div className="mt-6 text-center">
        <p className="text-blue-300 text-xs">Powered by</p>
        <Link to="/" className="text-white font-bold text-sm hover:underline">naukarihaat.com</Link>
        <p className="text-blue-400 text-[10px] mt-1">Your bridge to better work</p>
      </div>
    </div>
  );
}
